import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import { fetchEmployees, fetchEmployeeById, updateEmployee, deleteEmployee, fetchGetProjects } from "../../services/employeeService";
import { SearchDrawer } from "../../Component/SearchDrawer";
import CenteredCircularProgress from "../../Component/CenteredCircularProgress";
import { Employee } from "../../Interface/Employee";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Person } from "@mui/icons-material";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const Employees: React.FC = () => {
  const navigate = useNavigate();

  const [employeeData, setEmployeeData] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uniqueEmployeeCodes, setUniqueEmployeeCodes] = useState<string[]>([]);
  const [uniqueEmployeeNames, setUniqueEmployeeNames] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Employee | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [uniqueProjects, setUniqueProjects] = useState<string[]>([]);
  const [originalEmployeeData, setOriginalEmployeeData] = useState<Employee[]>([]);

  //Load Data
  const loadData = async () => {
    try {
      setLoading(true);
      const res = await fetchEmployees();
      const employeeArray = Array.isArray(res?.data) ? res.data : [];
      const activeMappings = employeeArray.filter(
        (item: any) =>
          item.isDeleted === false || item.isDeleted === 0 || item.isDeleted == null
      );
      setOriginalEmployeeData(activeMappings);
      setEmployeeData(activeMappings);

      setUniqueEmployeeCodes(Array.from(new Set(activeMappings.map((e: any) => e.employeeCode))));
      const sortedEmployeeNames = (Array.from(
        new Set(activeMappings.map((e: any) => e.employeeName))
      ) as string[]).sort((a, b) => a.localeCompare(b));
      setUniqueEmployeeNames(sortedEmployeeNames);

      const projectRes = await fetchGetProjects();

      const projectNames = Array.isArray(projectRes)
        ? projectRes.map((proj: any) => proj.projectName).filter((name: any) => !!name)
        : [];

      console.log("Loaded project names:", projectNames);
      setUniqueProjects(Array.from(new Set(projectNames)));

    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  //Handle Search
  const handleSearch = async (filters: Record<string, string>) => {
    console.log("Search filters received:", filters);
    const selectedProject = filters["project"] || filters["project:eq"];
    if (selectedProject) {
      const matchingEmployees = originalEmployeeData.filter(
        (emp) => emp.project === selectedProject
      );
      setEmployeeData(matchingEmployees);
      setDrawerOpen(false);
      return;
    }
    const selectedValue = Object.values(filters).find(Boolean);
    if (!selectedValue) return;
    const matchingEmployee = originalEmployeeData.find(
      (emp) =>
        emp.employeeCode === selectedValue ||
        emp.employeeName === selectedValue
    );
    if (!matchingEmployee) {
      setEmployeeData([]);
      setDrawerOpen(false);
      return;
    }
    try {
      setLoading(true);
      const employee = await fetchEmployeeById(matchingEmployee.employeeId);
      setEmployeeData(employee ? [employee] : []);
    } catch (error) {
      console.error("Error searching employee:", error);
    } finally {
      setLoading(false);
      setDrawerOpen(false);
    }
  };

  //Handle Edit
  const handleEditClick = (row: Employee) => {
    setSelectedRow({ ...row });
    setEditDialogOpen(true);
  };

  //Handle Delete
  const handleDeleteClick = (row: Employee) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  //Confirm Edit
  const confirmEdit = async () => {
    if (!selectedRow) return;
    const payload = {
      employeeId: selectedRow.employeeId,
      empCode: selectedRow.employeeCode,
      firstName: selectedRow.firstName,
      lastName: selectedRow.lastName,
      ctc: selectedRow.ctc,
      employeeType: selectedRow.employeeType || "Employee",
      isActive: true,
      isDeleted: false,
      createdOn: new Date().toISOString(),
      createdBy: 0,
      modifiedOn: new Date().toISOString(),
      modifiedBy: 0,
    };
    try {
      await updateEmployee(payload);
      setSnackbarMessage("Employee updated successfully");
      setSnackbarSeverity("success");
      setEditDialogOpen(false);
      await loadData();
    } catch (error) {
      setSnackbarMessage("Update failed");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  //Confirm Delete
  const confirmDelete = async () => {
    if (!selectedRow) return;
    try {
      const response = await deleteEmployee(selectedRow.employeeId);

      if (response?.data?.data === "Employee is deleted Successfully.") {
        setSnackbarMessage("Employee deleted successfully");
        setSnackbarSeverity("success");
        setDeleteDialogOpen(false);
        await loadData();
      } else {
        setSnackbarMessage("Cannot delete: Employee is mapped to a project.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      setSnackbarMessage("Delete failed");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  //Handle Edit Input Change - for Restriction in Textfield
  const handleEditInputChange = (field: keyof Employee, value: string) => {
    let newValue = value;
    if (field === "firstName" || field === "lastName") {
      newValue = newValue.replace(/[^a-zA-Z ]/g, "");
    }
    if (field === "ctc") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }
    if (field === "employeeCode") {
      newValue = newValue.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    }
    setSelectedRow((prev) => prev ? { ...prev, [field]: field === "ctc" ? Number(newValue) : newValue } : null);
  };

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title="Employee Master"
        icon={<Person />}
        button1Text="Add New"
        button1Action={() => navigate("/AddEmployee")}
        button2Text="Search"
        button2Action={() => setDrawerOpen(true)}
      />
      {loading ? (
        <CenteredCircularProgress />
      ) : (
        <>
          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            rowSelection="single"
            gridOptions={{}}
            columns={[
              { field: "employeeCode", headerName: "Employee Code", filter: true },
              { field: "firstName", headerName: "First Name", tooltipField: "firstName", filter: true },
              { field: "lastName", headerName: "Last Name", tooltipField: "lastName", filter: true },
              { field: "project", headerName: "Project", filter: true },
              {
                field: "ctc",
                headerName: "CTC",
                valueFormatter: (params: any) =>
                  params.value != null
                    ? `₹${new Intl.NumberFormat("en-IN").format(params.value)}`
                    : "",
                filter: true,
                cellStyle: { textAlign: "right" },
                headerClass: "ag-right-aligned-header",
                width: 120,
                minWidth: 100,
                maxWidth: 150,
              },
              {
                field: "actions",
                headerName: "Actions",
                cellStyle: { textAlign: "center" },
                minWidth: 120,
                maxWidth: 150,
                cellRenderer: (params: any) => (
                  <>
                    <IconButton onClick={() => handleEditClick(params.data)}>
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(params.data)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </>
                ),
              },
            ]}
            data={employeeData}
            handleSelectedRows={() => { }}
            noDataComponentText="No Employee data available"
            pagination={false}
            paginationPerPage={10}
          />
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogContent>
              <TextField
                label="Employee Code"
                fullWidth
                margin="normal"
                value={selectedRow?.employeeCode || ""}
                onChange={(e) => handleEditInputChange("employeeCode", e.target.value)}
              />

              <TextField
                label="First Name"
                fullWidth
                margin="normal"
                value={selectedRow?.firstName || ""}
                onChange={(e) => handleEditInputChange("firstName", e.target.value)}
              />

              <TextField
                label="Last Name"
                fullWidth
                margin="normal"
                value={selectedRow?.lastName || ""}
                onChange={(e) => handleEditInputChange("lastName", e.target.value)}
              />

              <TextField
                label="CTC"
                fullWidth
                margin="normal"
                value={selectedRow?.ctc || ""}
                onChange={(e) => handleEditInputChange("ctc", e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={confirmEdit} variant="contained">Update</Button>
              <Button onClick={() => setEditDialogOpen(false)} variant="outlined">Back</Button>
            </DialogActions>
          </Dialog>
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <b>{selectedRow?.firstName} {selectedRow?.lastName}</b>?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error">Delete</Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          <SearchDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSearch={handleSearch}
            onReset={loadData}
            filterFields={[
              {
                key: "employeeCode",
                label: "Employee Code",
                type: "dropdown",
                options: uniqueEmployeeCodes,
              },
              {
                key: "employeeName",
                label: "Employee Name",
                type: "dropdown",
                options: uniqueEmployeeNames,
              },
              {
                key: "project",
                label: "Project",
                type: "dropdown",
                options: uniqueProjects,
              },
            ]}
          />
        </>
      )}
    </Box>
  );
};

export default Employees;
